import { useState, useRef } from "react";
import { Link } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PropertyStatus } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Property } from "@shared/schema";
import { apiRequest } from '@/lib/api';
import { DUBAI_AREAS } from '@shared/areas';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  PlusCircle,
  Eye,
  Pencil,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  MapPin,
  Loader2,
  Sparkles,
  Airplay,
  Plus,
  X,
  Power,
  PowerOff,
  Upload,
  Trash2,
  Calendar,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import ManagerLayout from "@/components/layout/ManagerLayout";

export default function ManagerPropertiesPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddPropertyDialog, setShowAddPropertyDialog] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [uploadedImageUrls, setUploadedImageUrls] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formValues, setFormValues] = useState({
    area: "",
    address: "",
    title: "",
    description: "",
    bedrooms: "",
    bathrooms: "",
    maxGuests: "",
    basePrice: "",
    features: [] as string[],
    icalUrl: "",
    propertyType: "Apartment"
  });
  const [isSmartPricingLoading, setIsSmartPricingLoading] = useState(false);
  const [customAmenity, setCustomAmenity] = useState("");
  const [customAmenities, setCustomAmenities] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importType, setImportType] = useState<'single' | 'host'>('single');
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [isImportingPricelabs, setIsImportingPricelabs] = useState(false);
  const [importPricelabsError, setImportPricelabsError] = useState("");
  const pricelabsFileInputRef = useRef<HTMLInputElement>(null);
  const [showPricelabsConfirm, setShowPricelabsConfirm] = useState(false);

  // Fetch properties owned by the manager
  const queryClient = useQueryClient();
  const { data: properties = [], isLoading, error } = useQuery<Property[]>({
    queryKey: ["my-properties"],
    queryFn: async () => {
      console.log("Fetching properties with auth, user:", user);
      const data = await apiRequest('GET', 'my-properties');
      console.log("Properties data received:", data);
      return data;
    },
    enabled: !!user && (user.role.toLowerCase() === "manager" || user.role.toLowerCase() === "owner"),
    // refetchInterval: 5000 // Refetch every 5 seconds to ensure we see newly created properties
  });

  // Filter properties based on search term
  const filteredProperties = Array.isArray(properties) ? properties.filter(
    (property: any) =>
      property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.area.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];
  
  // Image upload handlers
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    
    addImages(Array.from(files));
  };
  
  const handleImageDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files.length) {
      addImages(Array.from(e.dataTransfer.files));
    }
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleImageContainerClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const addImages = (files: File[]) => {
    // Filter for only image files
    const imageFiles = files.filter(file => 
      file.type.match('image/jpeg') || 
      file.type.match('image/png') || 
      file.type.match('image/webp') ||
      file.type.match('image/jpg')
    );
    
    // Check if adding these would exceed the max of 20 images
    if (uploadedImages.length + imageFiles.length > 20) {
      toast({
        title: "Too many images",
        description: "You can upload a maximum of 20 images. Please remove some images first.",
        variant: "destructive",
      });
      return;
    }
    
    // Add files and create URLs
    setUploadedImages(prev => [...prev, ...imageFiles]);
    
    // Generate URLs for preview
    imageFiles.forEach(file => {
      const url = URL.createObjectURL(file);
      setUploadedImageUrls(prev => [...prev, url]);
    });
  };
  
  const removeImage = (index: number) => {
    // Revoke object URL to avoid memory leaks
    URL.revokeObjectURL(uploadedImageUrls[index]);
    
    // Remove image from arrays
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setUploadedImageUrls(prev => prev.filter((_, i) => i !== index));
  };
  
  // Form handling functions
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormValues(prev => ({ ...prev, [id]: value }));
    
    // Clear error for this field when user starts typing
    if (formErrors[id]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };
  
  const handleSelectChange = (id: string, value: string) => {
    setFormValues(prev => ({ ...prev, [id]: value }));
    
    // Clear error for this field when user selects a value
    if (formErrors[id]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };
  
  const handleAmenityChange = (amenity: string, checked: boolean) => {
    setFormValues(prev => {
      const features = [...prev.features];
      
      if (checked && !features.includes(amenity)) {
        features.push(amenity);
      } else if (!checked && features.includes(amenity)) {
        const index = features.indexOf(amenity);
        features.splice(index, 1);
      }
      
      return { ...prev, features };
    });
  };
  
  const handleAddCustomAmenity = () => {
    if (!customAmenity.trim()) return;
    
    // Add to custom amenities list
    setCustomAmenities(prev => [...prev, customAmenity]);
    
    // Add to form features
    setFormValues(prev => ({
      ...prev,
      features: [...prev.features, customAmenity]
    }));
    
    // Clear input
    setCustomAmenity("");
  };
  
  const handleRemoveCustomAmenity = (amenity: string) => {
    // Remove from custom amenities list
    setCustomAmenities(prev => prev.filter(item => item !== amenity));
    
    // Remove from form features
    setFormValues(prev => ({
      ...prev,
      features: prev.features.filter(item => item !== amenity)
    }));
  };
  
  // Form validation
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    // Required fields
    if (!formValues.area) errors.area = "Area is required";
    if (!formValues.address) errors.address = "Address is required";
    if (!formValues.description) errors.description = "Description is required";
    if (!formValues.bedrooms) errors.bedrooms = "Number of bedrooms is required";
    if (!formValues.bathrooms) errors.bathrooms = "Number of bathrooms is required";
    if (!formValues.maxGuests) errors.maxGuests = "Maximum guests is required";
    if (!formValues.basePrice) errors.basePrice = "Base price is required";
    
    // Numeric field validations
    if (formValues.maxGuests && Number(formValues.maxGuests) <= 0) {
      errors.maxGuests = "Maximum guests must be greater than 0";
    }
    
    if (formValues.basePrice && Number(formValues.basePrice) <= 0) {
      errors.basePrice = "Base price must be greater than 0";
    }
    
    // Image validation
    if (uploadedImages.length === 0) {
      errors.photos = "At least one photo is required";
    }
    
    // iCal URL validation (optional)
    if (formValues.icalUrl && !isValidUrl(formValues.icalUrl)) {
      errors.icalUrl = "Please enter a valid URL";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Helper function to validate URLs
  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: "Form Validation Error",
        description: "Please fill in all required fields correctly.",
        variant: "destructive",
      });
      return;
    }
    
    // Prevent multiple submissions
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Create property code based on area
      const areaCode = formValues.area.substring(0, 2).toUpperCase();
      const propertyCode = `DUB-${areaCode}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
      
      // Create form data for multipart/form-data upload with images
      const formData = new FormData();
      
      // Add all property details
      formData.append('title', propertyCode);
      formData.append('area', formValues.area);
      formData.append('address', formValues.address);
      formData.append('description', formValues.description);
      formData.append('bedrooms', formValues.bedrooms);
      formData.append('bathrooms', formValues.bathrooms);
      formData.append('maxGuests', formValues.maxGuests);
      formData.append('basePrice', formValues.basePrice);
      formData.append('features', JSON.stringify(formValues.features));
      
      if (formValues.icalUrl) {
        formData.append('iCalUrl', formValues.icalUrl);
      }
      
      // Add all images
      uploadedImages.forEach((image, index) => {
        formData.append('propertyImages', image);
      });
      
      // Submit to API
      const response = await apiRequest('POST', '/properties/create', formData);
      
      // if (!response.ok) {
      //   const errorData = await response.json().catch(() => ({}));
      //   throw new Error(errorData.message || 'Failed to submit property');
      // }
      
      // const data = await response.json();
      const data =  response;
      console.log("Property created:", data);
      
      // Success!
      toast({
        title: "Property Submitted Successfully",
        description: "Your property has been submitted and is awaiting admin approval.",
      });
      
      // Clean up image object URLs to prevent memory leaks
      uploadedImageUrls.forEach(url => URL.revokeObjectURL(url));
      
      // Close dialog and reset form
      setShowAddPropertyDialog(false);
      setUploadedImages([]);
      setUploadedImageUrls([]);
      setFormValues({
        title: '',
        area: '',
        address: '',
        description: '',
        bedrooms: '',
        bathrooms: '',
        maxGuests: '',
        basePrice: '',
        features: [],
        icalUrl: '',
        propertyType: 'Apartment',
      });
      setFormErrors({});
      
      // Invalidate the properties query to show the newly added property
      queryClient.invalidateQueries({ queryKey: ['my-properties'] });
    } catch (error: any) {
      console.error('Error submitting property:', error);
      
      toast({
        title: "Submission Failed",
        description: error.message || "There was an error submitting your property. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status: PropertyStatus) => {
    switch (status) {
      case PropertyStatus.ACTIVE:
        return <Badge className="bg-green-500 hover:bg-green-600">Active</Badge>;
      case PropertyStatus.PENDING:
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">Pending Approval</Badge>;
      case PropertyStatus.INACTIVE:
        return <Badge className="bg-gray-500 hover:bg-gray-600">Inactive</Badge>;
      case PropertyStatus.REJECTED:
        return <Badge className="bg-red-500 hover:bg-red-600">Rejected</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  return (
    <ManagerLayout>
      <div className="container py-6 max-w-7xl">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">My Properties</h1>
          <div className="flex gap-3">
            
            <Button
                variant="outline" 
                className="font-semibold" disabled={true}>
                  Sync with Hostaway
              </Button>
            {/* Import Pricelabs(CSV) Button */}
            <Button
              variant="outline"
              onClick={() => setShowPricelabsConfirm(true)}
              disabled={isImportingPricelabs}
            >
              {isImportingPricelabs ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" /> Import Pricelabs(CSV)
                </>
              )}
            </Button>
            <input
              type="file"
              ref={pricelabsFileInputRef}
              style={{ display: 'none' }}
              accept=".csv"
              title="Upload Pricelabs CSV"
              placeholder="Select a CSV file"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setIsImportingPricelabs(true);
                setImportPricelabsError("");
                try {
                  const formData = new FormData();
                  formData.append("file", file);
                  await apiRequest('POST', '/manager/import-pricelabs-csv', formData);
                  toast({ title: "Pricelabs import successful" });
                } catch (err: any) {
                  setImportPricelabsError(err?.message || 'Failed to import CSV');
                  toast({ title: "Pricelabs import failed", description: err?.message || 'Failed to import CSV', variant: 'destructive' });
                } finally {
                  setIsImportingPricelabs(false);
                }
              }}
            />
            <Link href="/manager/import-property">
              <Button variant="outline">
                <Airplay className="mr-2 h-4 w-4" /> Import Property
              </Button>
            </Link>
            <Dialog open={showAddPropertyDialog} onOpenChange={setShowAddPropertyDialog}>
              <DialogTrigger asChild>
                <Button>
                  <PlusCircle className="mr-2 h-4 w-4" /> Add New Property
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add New Property</DialogTitle>
                  <DialogDescription>
                    Enter all property details to create your new listing
                  </DialogDescription>
                </DialogHeader>
                <ScrollArea className="h-[70vh] pr-4" style={{ minHeight: '400px' }}>
                  <div className="space-y-6 p-4">
                    {/* Property details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="area">Area/Neighborhood *</Label>
                        <Select 
                          value={formValues.area}
                          onValueChange={(value) => handleSelectChange("area", value)}
                        >
                          <SelectTrigger id="area" className={formErrors.area ? "border-red-500" : ""}>
                            <SelectValue placeholder="Select area" />
                          </SelectTrigger>
                          <SelectContent>
                            {DUBAI_AREAS.map(area => (
                              <SelectItem key={area} value={area}>{area}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formErrors.area && <p className="text-sm text-red-500 mt-1">{formErrors.area}</p>}
                      </div>
                      
                      <div>
                        <Label htmlFor="propertyType">Property Type *</Label>
                        <Select 
                          value={formValues.propertyType}
                          onValueChange={(value) => handleSelectChange("propertyType", value)}
                        >
                          <SelectTrigger id="propertyType">
                            <SelectValue placeholder="Select property type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Apartment">Apartment</SelectItem>
                            <SelectItem value="Villa">Villa</SelectItem>
                            <SelectItem value="Townhouse">Townhouse</SelectItem>
                            <SelectItem value="Studio">Studio</SelectItem>
                            <SelectItem value="Penthouse">Penthouse</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="address">Full Address *</Label>
                      <Input 
                        id="address" 
                        placeholder="Complete property address" 
                        value={formValues.address}
                        onChange={handleInputChange}
                        className={formErrors.address ? "border-red-500" : ""}
                      />
                      {formErrors.address && <p className="text-sm text-red-500 mt-1">{formErrors.address}</p>}
                      <p className="text-sm text-muted-foreground mt-1">This will not be shown to guests until booking is confirmed.</p>
                    </div>
                    
                    <div>
                      <Label htmlFor="description">Property Description *</Label>
                      <Textarea 
                        id="description" 
                        placeholder="Describe your property in detail" 
                        rows={4}
                        value={formValues.description}
                        onChange={handleInputChange}
                        className={formErrors.description ? "border-red-500" : ""}
                      />
                      {formErrors.description && <p className="text-sm text-red-500 mt-1">{formErrors.description}</p>}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="bedrooms">Bedrooms *</Label>
                        <Input 
                          id="bedrooms" 
                          type="number" 
                          min="0"
                          placeholder="1" 
                          value={formValues.bedrooms}
                          onChange={handleInputChange}
                          className={formErrors.bedrooms ? "border-red-500" : ""}
                        />
                        {formErrors.bedrooms && <p className="text-sm text-red-500 mt-1">{formErrors.bedrooms}</p>}
                      </div>
                      
                      <div>
                        <Label htmlFor="bathrooms">Bathrooms *</Label>
                        <Input 
                          id="bathrooms" 
                          type="number"
                          min="0" 
                          placeholder="1" 
                          value={formValues.bathrooms}
                          onChange={handleInputChange}
                          className={formErrors.bathrooms ? "border-red-500" : ""}
                        />
                        {formErrors.bathrooms && <p className="text-sm text-red-500 mt-1">{formErrors.bathrooms}</p>}
                      </div>
                      
                      <div>
                        <Label htmlFor="maxGuests">Max Guests *</Label>
                        <Input 
                          id="maxGuests" 
                          type="number" 
                          min="1"
                          placeholder="2" 
                          value={formValues.maxGuests}
                          onChange={handleInputChange}
                          className={formErrors.maxGuests ? "border-red-500" : ""}
                        />
                        {formErrors.maxGuests && <p className="text-sm text-red-500 mt-1">{formErrors.maxGuests}</p>}
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="basePrice">Base Price (AED) *</Label>
                      <Input 
                        id="basePrice" 
                        type="number"
                        min="0" 
                        placeholder="500" 
                        value={formValues.basePrice}
                        onChange={handleInputChange}
                        className={formErrors.basePrice ? "border-red-500" : ""}
                      />
                      {formErrors.basePrice && <p className="text-sm text-red-500 mt-1">{formErrors.basePrice}</p>}
                      <p className="text-sm text-muted-foreground mt-1">Price per night in AED</p>
                    </div>
                    
                    <div>
                      <Label htmlFor="icalUrl">iCal URL (optional)</Label>
                      <Input 
                        id="icalUrl" 
                        placeholder="https://example.com/calendar.ics" 
                        value={formValues.icalUrl}
                        onChange={handleInputChange}
                        className={formErrors.icalUrl ? "border-red-500" : ""}
                      />
                      {formErrors.icalUrl && <p className="text-sm text-red-500 mt-1">{formErrors.icalUrl}</p>}
                      <p className="text-sm text-muted-foreground mt-1">Connect your property calendar from Airbnb, Booking.com, etc.</p>
                    </div>
                    
                    {/* Amenities section */}
                    <div>
                      <h3 className="text-lg font-medium mb-2">Property Amenities</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {["Wi-Fi", "Air Conditioning", "Pool", "Gym", "Parking", "Kitchen", "Washing Machine", "TV", "Balcony", "Beach Access", "City View"].map((amenity) => (
                          <div key={amenity} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`amenity-${amenity}`}
                              checked={formValues.features.includes(amenity)}
                              onChange={(e) => handleAmenityChange(amenity, e.target.checked)}
                              aria-label={`Select ${amenity} amenity`}
                              title={`Select ${amenity} amenity`}
                            />
                            <Label htmlFor={`amenity-${amenity}`} className="cursor-pointer">{amenity}</Label>
                          </div>
                        ))}
                      </div>
                      
                      {/* Custom amenity input */}
                      <div className="mt-4 flex gap-2">
                        <Input
                          placeholder="Add a custom amenity"
                          value={customAmenity}
                          onChange={(e) => setCustomAmenity(e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          onClick={handleAddCustomAmenity}
                          type="button"
                          variant="outline"
                          disabled={!customAmenity.trim()}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add
                        </Button>
                      </div>
                      
                      {/* Custom amenities tags */}
                      {customAmenities.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {customAmenities.map((amenity) => (
                            <Badge key={amenity} variant="outline" className="flex gap-1 items-center">
                              {amenity}
                              <button
                                type="button"
                                onClick={() => handleRemoveCustomAmenity(amenity)}
                                className="ml-1 text-muted-foreground hover:text-foreground"
                                aria-label={`Remove ${amenity} amenity`}
                                title={`Remove ${amenity} amenity`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Property images */}
                    <div>
                      <h3 className="text-lg font-medium mb-2">Property Images *</h3>
                      
                      {/* Image uploader */}
                      <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                          isDragging ? "border-primary bg-primary/5" : "border-border"
                        } ${formErrors.photos ? "border-red-500" : ""}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleImageDrop}
                        onClick={handleImageContainerClick}
                      >
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          onChange={handleImageUpload}
                          accept="image/*"
                          multiple
                          aria-label="Upload property images"
                          title="Upload property images"
                        />
                        <div className="flex flex-col items-center justify-center space-y-2">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Upload className="h-5 w-5 text-primary" />
                          </div>
                          <h4 className="text-sm font-medium">Upload Images</h4>
                          <p className="text-xs text-muted-foreground">
                            Drag & drop or click to upload (max 20 images)
                          </p>
                        </div>
                      </div>
                      {formErrors.photos && (
                        <p className="text-sm text-red-500 mt-1">{formErrors.photos}</p>
                      )}
                      
                      {/* Preview uploaded images */}
                      {uploadedImageUrls.length > 0 && (
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                          {uploadedImageUrls.map((url, index) => (
                            <div
                              key={index}
                              className="relative rounded-md overflow-hidden aspect-square group"
                            >
                              <img
                                src={url}
                                alt={`Property image ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeImage(index);
                                  }}
                                  className="text-white hover:text-red-500"
                                  aria-label={`Remove image ${index + 1}`}
                                  title={`Remove image ${index + 1}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                              {index === 0 && (
                                <div className="absolute top-1 left-1 bg-primary text-white px-2 py-0.5 rounded text-xs">
                                  Primary
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </ScrollArea>
                <div className="flex justify-end mt-4">
                  <Button 
                    onClick={handleSubmit} 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Create Property"
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        {/* Search input */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input 
            placeholder="Search properties by title or area..." 
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {/* Property listing */}
        <div className="space-y-6">
          {isLoading ? (
            <div className="flex justify-center items-center p-8">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" aria-label="Loading" />
            </div>
          ) : filteredProperties.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Area</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProperties.map((property: any) => (
                    <TableRow key={property.id}>
                      <TableCell className="font-medium">
                      <a href={`/properties/${property.id}`} target="_blank" rel="noopener noreferrer">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded overflow-hidden bg-gray-100">
                            {property.images && property.images.length > 0 ? (
                              <img 
                                src={property.images[0].imageUrl} 
                                alt={property.title} 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <MapPin className="w-full h-full p-2 text-muted-foreground" />
                            )}
                          </div>
                        
                              <span>{property.title}</span>
                              {/* <Eye className="w-5 h-5" /> */}
                        </div>
                      </a>
                      </TableCell>
                      <TableCell>
                        {property.area.charAt(0).toUpperCase() + property.area.slice(1)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(property.status as PropertyStatus)}
                      </TableCell>
                      <TableCell>
                        {new Date(property.createdAt).toLocaleDateString('en-US', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button size="icon" variant="ghost" asChild title="View Property">
                            <a href={`/properties/${property.id}`} target="_blank" rel="noopener noreferrer">
                              <Eye className="w-5 h-5" />
                            </a>
                          </Button>
                          <Button size="icon" variant="ghost" asChild title="Edit Property">
                            <a href={`/manager/edit-property/${property.id}`}>
                              <Pencil className="w-5 h-5" />
                            </a>
                          </Button>
                          <Button size="icon" variant="ghost" asChild title="Calendar">
                            <a href={`/manager/calendar/${property.id}`}>
                              <Calendar className="w-5 h-5" />
                            </a>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2">No properties yet</h3>
              <p className="text-muted-foreground mb-6">Click 'Add New Property' to get started</p>
            </div>
          )}
        </div>
      </div>
      {/* Confirmation Dialog for Pricelabs Import */}
      <Dialog open={showPricelabsConfirm} onOpenChange={setShowPricelabsConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Pricelabs Import</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p>
              <b>Warning:</b> This will <span className="text-red-600 font-semibold">replace all prices</span> of all properties from price labs with the available property dates for the next 365 days with the recommended base price from Pricelabs.<br/>
              <br></br>Are you sure you want to continue?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPricelabsConfirm(false)}>Cancel</Button>
            <Button
              onClick={() => {
                setShowPricelabsConfirm(false);
                if (pricelabsFileInputRef.current) {
                  pricelabsFileInputRef.current.value = '';
                  pricelabsFileInputRef.current.click();
                }
              }}
            >Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ManagerLayout>
  );
}